import { NgTemplateOutlet } from '@angular/common';
import type { AfterContentInit, ElementRef, SimpleChanges } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  Optional,
  Renderer2,
  Self,
  ViewChild,
  output,
  input,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';

import { takeUntil } from 'rxjs/operators';

import { TextareaAutosizeDirective } from './textarea-autosize.directive';
import { IconComponent } from '../../common/icon/icon.component';
import type { IconName } from '../../common/icon/icons.type';
import { RtTextInput } from '../../utils/rt-input';
import { ComponentVariantTransform, type Variants } from '../../utils/variant/variant';
import { WindowRef } from '../../utils/window-ref';

@Component({
  selector: 'rt-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.less'],
  host: {
    '[class.disabled]': 'disabled',
    '[style.width]': 'width()',
    '[style.height]': 'height()',
    '[class.invalid]': 'invalid()',
    '[class.padding-small]': 'paddingSize() === "small"',
    '[class.padding-small-x]': 'paddingSize() === "small-x"',
    '[class.padding-medium]': 'paddingSize() === "medium"',
    '[class.private-background]': 'hasPrivateBackground()',
    '[class.has-right-padding]': 'hasRightPadding()',
    '[class.with-icon]': '!!icon',
    '[class.icon-red-on-hover]': '!!icon && iconRedOnHover()',
    '[class.has-font-weight-default]': 'hasFontWeightDefault()',
    '[class]': 'variant()',
    '[class.readonly]': 'readOnly()',
  },
  providers: [
    {
      provide: RtTextInput,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => TextareaComponent),
    },
  ],
  imports: [TextareaAutosizeDirective, NgTemplateOutlet, IconComponent],
})
export class TextareaComponent extends RtTextInput implements AfterContentInit {
  public static readonly VARIANTS: Variants = ['rounded', ['rounded', 'plain']];
  public readonly variant = input(...ComponentVariantTransform(TextareaComponent.VARIANTS));
  public readonly placeholder = input<string>(undefined!);
  public readonly maxLength = input<number>(undefined!);
  public readonly tabIndex = input<number>(undefined!);
  public readonly charactersLimit = input<number>(undefined!);
  public readonly width = input<string>('auto');
  public readonly height = input<string>('auto');
  public readonly invalid = input<boolean>(undefined!);
  public readonly maxHeight = input<number | null>(null);
  public readonly autoFocus = input<boolean>(false);
  public readonly autoFocusPreventScroll = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  public readonly autoHeight = input<boolean>(false);
  public readonly hasPrivateBackground = input<boolean>(false);
  public readonly defaultHeight = input<number>(55);
  public readonly rows = input<number>(2);
  public readonly paddingSize = input<'small-x' | 'small' | 'medium'>(undefined!);
  public readonly autosize = input<boolean>(undefined!);
  public readonly autosizeMinRows = input<number>(undefined!);
  public readonly autosizeMaxRows = input<number>(undefined!);
  public readonly hasRightPadding = input<boolean>(false);
  public readonly iconRedOnHover = input<boolean>(false);
  public readonly hasFontWeightDefault = input<boolean>(false);
  public readonly iconLight = input<boolean>(false);
  public readonly overflowHidden = input<boolean>(false);

  public iconClick = output<void>();

  @Input() public icon: IconName | null = null;

  @ViewChild('textarea', { static: true }) private textarea: ElementRef<HTMLTextAreaElement>;
  @ViewChild('textareaWrap', { static: true }) private textareaWrap: ElementRef;

  constructor(
    private renderer2: Renderer2,
    private window: WindowRef,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public get controlValue(): string | undefined {
    return this.ngControl?.control?.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.setValue(this.value!);
    }

    if (changes.autoFocus && changes.autoFocus.currentValue) {
      this.setFocus();
    }
  }

  public ngAfterContentInit(): void {
    if (this.autoFocus()) {
      this.setFocus();
    }

    if (this.autoHeight()) {
      this.adjustInputHeight(this.value);

      outputToObservable(this.valueChange)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(value => this.adjustInputHeight(value));
    }
  }

  public getNativeInputElement(): HTMLTextAreaElement {
    return this.textarea.nativeElement;
  }

  public setFocus(): void {
    this.textarea.nativeElement.focus({ preventScroll: this.autoFocusPreventScroll() });
  }

  public onFocus(event: UIEvent): void {
    this.focus.emit(event);
    this.renderer2.addClass(this.textareaWrap.nativeElement, 'has-focus');
  }

  public onBlur(event: UIEvent): void {
    this.blur.emit(event);
    this.renderer2.removeClass(this.textareaWrap.nativeElement, 'has-focus');
  }

  protected setValue(value: string): void {
    this.value = value;
    if (this.textarea.nativeElement.value !== value) {
      this.textarea.nativeElement.value = value || '';
    }
  }

  private adjustInputHeight(value: string | null): void {
    const computedStyle = this.window.nativeWindow.getComputedStyle(this.textarea.nativeElement);
    const topPadding = parseInt(
      computedStyle.getPropertyValue('padding-top').replace('px', ''),
      10,
    );
    const bottomPadding = parseInt(
      computedStyle.getPropertyValue('padding-bottom').replace('px', ''),
      10,
    );

    const maxHeight = this.maxHeight();
    if (maxHeight !== null) {
      this.textarea.nativeElement.style.maxHeight = maxHeight + 'px';
    } else {
      this.textarea.nativeElement.style.overflow = 'hidden';
    }

    this.textarea.nativeElement.style.height = 'auto';
    const computedHeight = value
      ? Math.max(
          this.textarea.nativeElement.scrollHeight - topPadding - bottomPadding,
          this.defaultHeight(),
        )
      : this.defaultHeight();
    this.textarea.nativeElement.style.height = computedHeight + 'px';
  }
}
